import { Deferred, router, usePage } from "@inertiajs/react";
import { CheckIcon, ChevronsUpDown, SearchIcon, X, XIcon } from "lucide-react";
import { type ComponentProps, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { reasonLabel } from "@/lib/appointments/reason-label";
import { cn, debounce, populateQueryParams } from "@/lib/utils";
import type {
	AppointmentDraftsGlobalPageProps,
	StatusReason,
} from "@/pages/AdminPortal/Appointment/Drafts";
import DateRangeFilter, {
	BACKWARD_PRESETS,
	BIDIRECTIONAL_PRESETS,
	formatDayRangeLabel,
} from "./date-range-filter";

// The two filters that are never empty. The page opens with these values, and the
// controller falls back to them when the params are absent, so they are the "no filter
// applied" state — they must not show up as active-filter pills. Keep them in step with
// AdminPortal::AppointmentDraftsController::DEFAULT_STATUS: if the two drift, the pill
// row claims a narrowing the server did not apply.
const DEFAULT_ADMIN_ID = "me";
const DEFAULT_STATUS = "all";

const EMPTY_FILTERS = {
	draftId: "",
	patient: "",
	city: "",
	// Comma-separated because populateQueryParams writes one value per key, so a real
	// array param would collapse to its last entry. The controller splits on the comma.
	tags: "",
	statusReason: "",
	status: DEFAULT_STATUS,
	adminId: DEFAULT_ADMIN_ID,
	createdFrom: "",
	createdTo: "",
	scheduledFrom: "",
	scheduledTo: "",
};

// Each date filter is one control over two params, so its pill has to dismiss both ends
// at once — clearing only `from` would leave a half-range the admin never asked for.
//
// The two share a control but not a direction in time: a Draft can only have been created
// already, whereas an appointment is normally scheduled ahead, so each carries the preset
// list that points the way its own dates run.
const DATE_RANGE_FILTERS = [
	{
		key: "created",
		fromKey: "createdFrom",
		toKey: "createdTo",
		labelKey: "drafts.filters.labels.created_at",
		placeholderKey: "drafts.filters.created_range_placeholder",
		presets: BACKWARD_PRESETS,
	},
	{
		key: "scheduled",
		fromKey: "scheduledFrom",
		toKey: "scheduledTo",
		labelKey: "drafts.filters.labels.schedule",
		placeholderKey: "drafts.filters.schedule_range_placeholder",
		presets: BIDIRECTIONAL_PRESETS,
	},
] as const;

export type DraftFilters = typeof EMPTY_FILTERS;

// Module scope: this touches no component state, so keeping it here means the debounced
// wrapper below has nothing changing underneath it.
const visit = (url: string) =>
	router.get(
		url,
		{},
		{
			preserveScroll: true,
			preserveState: true,
			replace: true,
			only: ["adminPortal", "flash", "errors", "drafts"],
		},
	);

export interface DraftFilterListProps extends ComponentProps<"div"> {
	onToggleShow: () => void;
}

export default function DraftFilterList({
	className,
	onToggleShow,
}: DraftFilterListProps) {
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentDraftsGlobalPageProps>();
	const { t, i18n } = useTranslation("appointments");
	const lng = i18n.language;
	const { locale: dateFnsLocale } = useDateContext();

	const [popoverOpen, setPopoverOpen] = useState(false);
	const [tagsOpen, setTagsOpen] = useState(false);
	const [filters, setFilters] = useState<DraftFilters>(() => {
		const currentQuery = globalProps?.adminPortal?.currentQuery;
		return {
			draftId: currentQuery?.draftId || "",
			patient: currentQuery?.patient || "",
			city: currentQuery?.city || "",
			tags: currentQuery?.tags || "",
			statusReason: currentQuery?.statusReason || "",
			status: currentQuery?.status || DEFAULT_STATUS,
			adminId: currentQuery?.adminId || DEFAULT_ADMIN_ID,
			createdFrom: currentQuery?.createdFrom || "",
			createdTo: currentQuery?.createdTo || "",
			scheduledFrom: currentQuery?.scheduledFrom || "",
			scheduledTo: currentQuery?.scheduledTo || "",
		};
	});

	// Empty deps so the debounce timer survives re-renders — recreating it on every
	// keystroke would defeat the debounce entirely.
	const debouncedVisit = useCallback(
		debounce((url: string) => visit(url), 250),
		[],
	);

	const goToFilteredUrl = useCallback(
		(nextFilters: DraftFilters, immediate = false) => {
			const { fullUrl } = populateQueryParams(
				pageURL,
				deepTransformKeysToSnakeCase({
					...nextFilters,
					// null removes the param. Paging and expansion belong to the previous
					// result set, so they cannot survive a filter change.
					page: null,
					limit: null,
					expanded: null,
				}),
			);

			if (immediate) visit(fullUrl);
			else debouncedVisit(fullUrl);
		},
		[pageURL, debouncedVisit],
	);

	// Takes a patch rather than a single key because a date range moves both of its ends
	// together; applying them one at a time would fire two visits and briefly filter on a
	// range the admin never selected.
	const handleFilterPatch = useCallback(
		(patch: Partial<DraftFilters>) => {
			const nextFilters = { ...filters, ...patch };
			setFilters(nextFilters);
			goToFilteredUrl(nextFilters);
		},
		[filters, goToFilteredUrl],
	);

	const handleFilterBy = useCallback(
		({ value, type }: { value: string; type: keyof DraftFilters }) =>
			handleFilterPatch({ [type]: value }),
		[handleFilterPatch],
	);

	const handleResetAll = useCallback(() => {
		setFilters(EMPTY_FILTERS);
		goToFilteredUrl(EMPTY_FILTERS, true);
	}, [goToFilteredUrl]);

	const selectedAdminName = useCallback(
		(adminId: string) =>
			globalProps.admins?.find((admin) => String(admin.id) === adminId)?.name,
		[globalProps.admins],
	);

	const statusLabel = useCallback(
		(status: string) =>
			({
				expired: t("drafts.filters.status_expired"),
				cancelled: t("drafts.filters.status_cancelled"),
				scheduled: t("drafts.filters.status_scheduled"),
				all: t("drafts.filters.status_all"),
			})[status] || t("drafts.filters.status_active"),
		[t],
	);

	const selectedTags = useMemo(
		() => (filters.tags ? filters.tags.split(",") : []),
		[filters.tags],
	);

	// The badge translations are the filter's labels — the same words in the row and in
	// the filter, so there is nothing to learn twice. Wire keys are kebab-case
	// (AppointmentDraft::TAG_FILTERS), translation keys snake_case.
	const tagLabel = useCallback(
		(tag: string) =>
			t(`drafts.badges.${tag.replace(/-/g, "_")}`, { defaultValue: tag }),
		[t],
	);

	const toggleTag = useCallback(
		(tag: string) => {
			const next = selectedTags.includes(tag)
				? selectedTags.filter((selected) => selected !== tag)
				: [...selectedTags, tag];
			// Debounced like every other filter, which here is a feature: the popover stays
			// open, so ticking two tags in a row costs one request instead of two.
			handleFilterPatch({ tags: next.join(",") });
		},
		[selectedTags, handleFilterPatch],
	);

	const ownershipLabel = useCallback(
		(adminId: string) => {
			if (adminId === "me") return t("drafts.filters.assigned_me");
			if (adminId === "all") return t("drafts.filters.all");
			if (adminId === "unassigned") return t("drafts.filters.unassigned");
			return selectedAdminName(adminId) || t("drafts.filters.pic_placeholder");
		},
		[t, selectedAdminName],
	);

	// Reads the stored wire format through the same helper the picker's trigger uses, so
	// neither can drift onto its own date format. The trigger goes one step further and
	// swaps in a preset's name when the range happens to be one; the pill deliberately
	// stays on the dates, because what it offers is dismissal and you should see exactly
	// which days you are dropping.
	const dateRangeLabel = useCallback(
		(from: string, to: string) =>
			formatDayRangeLabel(from, to, t, dateFnsLocale),
		[t, dateFnsLocale],
	);

	// Only filters that differ from the page defaults become pills. Status and ownership
	// always hold a value, so including them unconditionally would mean the pill row is
	// never empty and never signals "you have narrowed something".
	const activeFilters = useMemo(() => {
		const result: {
			key: string;
			keys: (keyof DraftFilters)[];
			label: string;
			value: string;
		}[] = [];

		if (filters.draftId) {
			result.push({
				key: "draftId",
				keys: ["draftId"],
				label: t("drafts.filters.labels.draft_id"),
				value: `#${filters.draftId.replace(/^#/, "")}`,
			});
		}
		if (filters.patient) {
			result.push({
				key: "patient",
				keys: ["patient"],
				label: t("drafts.filters.labels.patient"),
				value: filters.patient,
			});
		}
		if (filters.city) {
			result.push({
				key: "city",
				keys: ["city"],
				label: t("drafts.filters.labels.region"),
				value: filters.city,
			});
		}
		if (selectedTags.length) {
			result.push({
				key: "tags",
				keys: ["tags"],
				label: t("drafts.filters.labels.tags"),
				// One pill for the whole facet, dismissed as a whole — the same shape as a
				// date range, and the tags OR together so half a selection means something
				// different from what the admin picked.
				value: selectedTags.map(tagLabel).join(", "),
			});
		}
		if (filters.statusReason) {
			result.push({
				key: "statusReason",
				keys: ["statusReason"],
				label: t("drafts.filters.labels.status_reason"),
				value:
					globalProps?.statusReasons?.find(
						(reason: StatusReason) => reason.value === filters.statusReason,
					)?.[lng === "id" ? "labelId" : "labelEn"] || filters.statusReason,
			});
		}
		if (filters.status !== DEFAULT_STATUS) {
			result.push({
				key: "status",
				keys: ["status"],
				label: t("drafts.filters.labels.status"),
				value: statusLabel(filters.status),
			});
		}
		if (filters.adminId !== DEFAULT_ADMIN_ID) {
			result.push({
				key: "adminId",
				keys: ["adminId"],
				label: t("drafts.filters.labels.pic"),
				value: ownershipLabel(filters.adminId),
			});
		}

		for (const range of DATE_RANGE_FILTERS) {
			const from = filters[range.fromKey];
			const to = filters[range.toKey];
			if (!from && !to) continue;

			result.push({
				key: range.key,
				keys: [range.fromKey, range.toKey],
				label: t(range.labelKey),
				value: dateRangeLabel(from, to),
			});
		}

		return result;
	}, [
		filters,
		t,
		lng,
		globalProps?.statusReasons,
		statusLabel,
		ownershipLabel,
		dateRangeLabel,
		selectedTags,
		tagLabel,
	]);

	// Dismissing a pill restores its filters to their defaults, which for status and
	// ownership is a value rather than "". A date range restores both ends at once.
	const clearFilter = useCallback(
		(keys: (keyof DraftFilters)[]) => {
			handleFilterPatch(
				Object.fromEntries(keys.map((key) => [key, EMPTY_FILTERS[key]])),
			);
		},
		[handleFilterPatch],
	);

	return (
		<div
			className={cn(
				"p-4 border shadow-inner rounded-xl bg-background",
				className,
			)}
		>
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
				<div className="relative">
					<SearchIcon className="z-10 absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/75" />
					<Input
						type="text"
						placeholder={t("drafts.filters.search_placeholder")}
						className="pl-8 shadow-inner bg-input"
						value={filters.draftId}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							handleFilterBy({ value: event.target.value, type: "draftId" });
						}}
					/>

					{filters.draftId && (
						// biome-ignore lint/a11y/useSemanticElements: act like button
						<div
							role="button"
							tabIndex={0}
							onClick={() => clearFilter(["draftId"])}
							onKeyUp={() => {}}
							className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
						>
							<X className="h-4 w-4" />
						</div>
					)}
				</div>

				<div className="relative">
					<SearchIcon className="z-10 absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/75" />
					<Input
						type="text"
						placeholder={t("drafts.filters.patient_placeholder")}
						className="pl-8 shadow-inner bg-input"
						value={filters.patient}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							handleFilterBy({ value: event.target.value, type: "patient" });
						}}
					/>

					{filters.patient && (
						// biome-ignore lint/a11y/useSemanticElements: act like button
						<div
							role="button"
							tabIndex={0}
							onClick={() => clearFilter(["patient"])}
							onKeyUp={() => {}}
							className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
						>
							<X className="h-4 w-4" />
						</div>
					)}
				</div>

				<Deferred
					data={["filterableCities"]}
					fallback={<Skeleton className="w-full rounded-md h-10" />}
				>
					<Select
						value={filters.city || "all"}
						onValueChange={(value) => {
							handleFilterBy({
								value: value === "all" ? "" : value,
								type: "city",
							});
						}}
					>
						<SelectTrigger className="shadow-inner bg-input">
							<SelectValue placeholder={t("drafts.filters.region_placeholder")}>
								{filters.city || t("drafts.filters.all_regions")}
							</SelectValue>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">
								{t("drafts.filters.all_regions")}
							</SelectItem>
							{globalProps?.filterableCities?.map((city) => (
								<SelectItem key={city} value={city}>
									{city}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</Deferred>

				{/*
					A Popover of toggles rather than a Select: Radix's Select closes on every
					pick and holds one value, and this facet is multi-select. The PIC combobox
					next to it is the same Command shape, minus the "close on select".
				*/}
				<Popover open={tagsOpen} onOpenChange={setTagsOpen}>
					<PopoverTrigger asChild>
						<Button
							variant="outline"
							aria-expanded={tagsOpen}
							className="shadow-inner bg-input justify-between font-normal"
						>
							<span className="line-clamp-1 truncate">
								{selectedTags.length
									? selectedTags.map(tagLabel).join(", ")
									: t("drafts.filters.all_tags")}
							</span>
							<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
						</Button>
					</PopoverTrigger>
					<PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]">
						<Command>
							<CommandList>
								<CommandEmpty>{t("drafts.filters.no_tags")}</CommandEmpty>
								<CommandGroup>
									{globalProps.draftTags?.map((tag) => (
										<CommandItem
											key={tag}
											value={tag}
											onSelect={() => toggleTag(tag)}
											className="justify-between"
										>
											{tagLabel(tag)}
											<CheckIcon
												className={cn(
													"h-4 w-4",
													selectedTags.includes(tag)
														? "opacity-100"
														: "opacity-0",
												)}
											/>
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>

				<Select
					value={filters.statusReason || "all"}
					onValueChange={(value) => {
						handleFilterBy({
							value: value === "all" ? "" : value,
							type: "statusReason",
						});
					}}
				>
					<SelectTrigger className="shadow-inner bg-input">
						<SelectValue
							placeholder={t("drafts.filters.status_reason_placeholder")}
						/>
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">
							{t("drafts.filters.all_status_reasons")}
						</SelectItem>
						{globalProps?.statusReasons?.map((reason: StatusReason) => (
							<SelectItem key={reason.value} value={reason.value}>
								{reasonLabel(reason, lng)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				<Select
					value={filters.status}
					onValueChange={(value) => handleFilterBy({ value, type: "status" })}
				>
					<SelectTrigger className="shadow-inner bg-input">
						<SelectValue placeholder={t("drafts.filters.status_placeholder")}>
							{statusLabel(filters.status)}
						</SelectValue>
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="active">
							{t("drafts.filters.status_active")}
						</SelectItem>
						<SelectItem value="expired">
							{t("drafts.filters.status_expired")}
						</SelectItem>
						<SelectItem value="cancelled">
							{t("drafts.filters.status_cancelled")}
						</SelectItem>
						<SelectItem value="scheduled">
							{t("drafts.filters.status_scheduled")}
						</SelectItem>
						<SelectItem value="all">
							{t("drafts.filters.status_all")}
						</SelectItem>
					</SelectContent>
				</Select>

				<Deferred
					data={["admins"]}
					fallback={<Skeleton className="w-full rounded-md h-10" />}
				>
					<Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								aria-expanded={popoverOpen}
								className="shadow-inner bg-input justify-between"
							>
								<span className="line-clamp-1 truncate">
									{ownershipLabel(filters.adminId)}
								</span>
								<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]">
							<Command>
								<CommandInput placeholder={t("drafts.filters.search_admin")} />
								<CommandList>
									<CommandEmpty>{t("drafts.filters.no_admin")}</CommandEmpty>
									<CommandGroup>
										{["all", "me", "unassigned"].map((value) => (
											<CommandItem
												key={value}
												value={value}
												onSelect={() => {
													handleFilterBy({ value, type: "adminId" });
													setPopoverOpen(false);
												}}
												className="justify-between"
											>
												{ownershipLabel(value)}
												<CheckIcon
													className={cn(
														"h-4 w-4",
														filters.adminId === value
															? "opacity-100"
															: "opacity-0",
													)}
												/>
											</CommandItem>
										))}

										{globalProps.admins?.map((admin) => (
											<CommandItem
												key={admin.id}
												value={admin.name}
												onSelect={() => {
													handleFilterBy({
														value: String(admin.id),
														type: "adminId",
													});
													setPopoverOpen(false);
												}}
											>
												<div className="flex gap-2 flex-1">
													<Avatar className="size-6 !rounded-[0px] text-primary border border-primary">
														<AvatarFallback className="text-xs !rounded-[0px]">
															{admin.name?.charAt(0)?.toUpperCase() || "A"}
														</AvatarFallback>
													</Avatar>

													<div className="flex flex-col">
														<span className="text-sm text-pretty">
															{admin.name}
														</span>
														{admin?.user?.email && (
															<span className="text-xs text-muted-foreground">
																{admin.user.email}
															</span>
														)}
													</div>
												</div>

												<CheckIcon
													className={cn(
														"h-4 w-4",
														filters.adminId === String(admin.id)
															? "opacity-100"
															: "opacity-0",
													)}
												/>
											</CommandItem>
										))}
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
				</Deferred>

				{DATE_RANGE_FILTERS.map((range) => (
					<DateRangeFilter
						key={range.key}
						value={{
							from: filters[range.fromKey],
							to: filters[range.toKey],
						}}
						onChange={(next) =>
							handleFilterPatch({
								[range.fromKey]: next.from,
								[range.toKey]: next.to,
							})
						}
						placeholder={t(range.placeholderKey)}
						presets={range.presets}
					/>
				))}
			</div>

			{activeFilters.length > 0 && (
				<div className="flex flex-wrap items-center gap-2 mt-6">
					<span className="text-xs font-medium text-muted-foreground">
						{t("drafts.filters.active_filters")}:
					</span>

					{activeFilters.map((filter) => (
						<Badge
							key={filter.key}
							variant="secondary"
							className="flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-[10px] uppercase tracking-wide"
						>
							<span>{filter.label}:</span>
							<span className="font-bold">{filter.value}</span>
							<button
								type="button"
								onClick={() => clearFilter(filter.keys)}
								className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted-foreground/15"
							>
								<XIcon className="h-3 w-3" />
							</button>
						</Badge>
					))}

					<Button
						variant="destructive-outline"
						size="sm"
						className="rounded-full"
						onClick={handleResetAll}
					>
						{t("button.clear_all_filters")}
					</Button>
				</div>
			)}

			<div className="flex flex-col justify-end gap-2 mt-4 md:flex-row">
				<Button
					variant="ghost"
					size="sm"
					className="w-full md:w-fit"
					onClick={onToggleShow}
				>
					{t("button.close_filter")}
				</Button>
				<Button
					variant="ghost-destructive"
					size="sm"
					className="w-full md:w-fit"
					onClick={handleResetAll}
				>
					{t("button.clear_all_filters")}
				</Button>
			</div>
		</div>
	);
}
